import { profileSchema, type Profile } from "@contracts";

const PROFILE_KEY = "bakkwimpyo.profile.v1";
const WATCHLIST_KEY = "bakkwimpyo.watchlist.v1";
const CORRECTION_KEY = "bakkwimpyo.corrections.v1";
const RECENT_QUESTIONS_KEY = "bakkwimpyo.questions.v1";
const LAST_SEEN_UPDATES_KEY = "bakkwimpyo.updates.last-seen.v1";
const WATCH_SEEN_KEY = "bakkwimpyo.watch-seen.v1";
const SAVED_ACTIONS_KEY = "bakkwimpyo.saved-actions.v1";

export interface CorrectionReceipt {
  id: string;
  policyTitle: string;
  issue: string;
  officialUrl: string | null;
  status: "local_draft";
  createdAt: string;
}

export interface RecentQuestion {
  query: string;
  askedAt: string;
}

export interface SavedAction {
  id: string;
  query: string;
  label: string;
  url: string | null;
  dueAt: string | null;
  status: "pending" | "done";
  savedAt: string;
}

export const emptyProfile: Profile = profileSchema.parse({});

export function readProfile(): Profile {
  try {
    const value = localStorage.getItem(PROFILE_KEY);
    return value ? profileSchema.parse(JSON.parse(value)) : emptyProfile;
  } catch {
    return emptyProfile;
  }
}

export function writeProfile(profile: Profile): Profile {
  const safe = profileSchema.parse(profile);
  localStorage.setItem(PROFILE_KEY, JSON.stringify(safe));
  window.dispatchEvent(new Event("bakkwimpyo:profile"));
  return safe;
}

export function deleteProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
  window.dispatchEvent(new Event("bakkwimpyo:profile"));
}

export function exportProfile(): string {
  return JSON.stringify({schemaVersion: 1, exportedAt: new Date().toISOString(), profile: readProfile()}, null, 2);
}

export function readWatchlist(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(WATCHLIST_KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

export function toggleWatch(policyId: string): string[] {
  const current = new Set(readWatchlist());
  current.has(policyId) ? current.delete(policyId) : current.add(policyId);
  const next = [...current];
  localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("bakkwimpyo:watchlist"));
  return next;
}

export function deleteAllLocalData(): void {
  localStorage.removeItem(PROFILE_KEY);
  localStorage.removeItem(WATCHLIST_KEY);
  localStorage.removeItem(CORRECTION_KEY);
  localStorage.removeItem(RECENT_QUESTIONS_KEY);
  localStorage.removeItem(LAST_SEEN_UPDATES_KEY);
  localStorage.removeItem(WATCH_SEEN_KEY);
  localStorage.removeItem(SAVED_ACTIONS_KEY);
  window.dispatchEvent(new Event("bakkwimpyo:profile"));
  window.dispatchEvent(new Event("bakkwimpyo:watchlist"));
  window.dispatchEvent(new Event("bakkwimpyo:corrections"));
}

function actionId(query: string, label: string): string {
  let value = 2166136261;
  for (const char of `${query}\u0000${label}`) {
    value ^= char.codePointAt(0) ?? 0;
    value = Math.imul(value, 16777619);
  }
  return `action-${(value >>> 0).toString(16).padStart(8, "0")}`;
}

export function readSavedActions(): SavedAction[] {
  try {
    const value = JSON.parse(localStorage.getItem(SAVED_ACTIONS_KEY) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is SavedAction =>
      typeof item?.id === "string" &&
      typeof item?.query === "string" &&
      typeof item?.label === "string" &&
      (item?.url === null || typeof item?.url === "string") &&
      (item?.dueAt === null || typeof item?.dueAt === "string") &&
      (item?.status === "pending" || item?.status === "done") &&
      typeof item?.savedAt === "string"
    ).slice(0, 20);
  } catch {
    return [];
  }
}

export function saveAction(input: {query: string; label: string; url: string | null; dueAt?: string | null; savedAt?: string}): SavedAction[] {
  const query = input.query.trim().slice(0, 500);
  const label = input.label.trim().slice(0, 160);
  if (query.length < 2 || label.length < 2) throw new Error("invalid_saved_action");
  const id = actionId(query, label);
  const current = readSavedActions();
  const existing = current.find((item) => item.id === id);
  const row: SavedAction = existing ?? {id, query, label, url: input.url, dueAt: input.dueAt ?? null, status: "pending", savedAt: input.savedAt ?? new Date().toISOString()};
  const next = [row, ...current.filter((item) => item.id !== id)].slice(0, 20);
  localStorage.setItem(SAVED_ACTIONS_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("bakkwimpyo:saved-actions"));
  return next;
}

export function toggleSavedAction(id: string): SavedAction[] {
  const next = readSavedActions().map((item) => item.id === id ? {...item, status: item.status === "pending" ? "done" as const : "pending" as const} : item);
  localStorage.setItem(SAVED_ACTIONS_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("bakkwimpyo:saved-actions"));
  return next;
}

export function deleteSavedAction(id: string): SavedAction[] {
  const next = readSavedActions().filter((item) => item.id !== id);
  localStorage.setItem(SAVED_ACTIONS_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("bakkwimpyo:saved-actions"));
  return next;
}

export function readRecentQuestions(now = new Date()): RecentQuestion[] {
  try {
    const rows = JSON.parse(localStorage.getItem(RECENT_QUESTIONS_KEY) ?? "[]");
    if (!Array.isArray(rows)) return [];
    const expiresBefore = now.getTime() - 30 * 24 * 60 * 60 * 1000;
    const safe = rows.filter((row): row is RecentQuestion => typeof row?.query === "string" && typeof row?.askedAt === "string" && Number.isFinite(Date.parse(row.askedAt)) && Date.parse(row.askedAt) >= expiresBefore).slice(0, 5);
    if (safe.length !== rows.length) localStorage.setItem(RECENT_QUESTIONS_KEY, JSON.stringify(safe));
    return safe;
  } catch {
    return [];
  }
}

export function rememberQuestion(query: string, askedAt = new Date().toISOString()): RecentQuestion[] {
  const safe = query.trim().slice(0, 500);
  if (safe.length < 2) return readRecentQuestions();
  const next = [{query: safe, askedAt}, ...readRecentQuestions(new Date(askedAt)).filter((row) => row.query !== safe)].slice(0, 5);
  localStorage.setItem(RECENT_QUESTIONS_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("bakkwimpyo:questions"));
  return next;
}

export function readLastSeenUpdate(): string | null {
  return localStorage.getItem(LAST_SEEN_UPDATES_KEY);
}

export function markUpdatesSeen(revision: string): void {
  localStorage.setItem(LAST_SEEN_UPDATES_KEY, revision);
  window.dispatchEvent(new Event("bakkwimpyo:updates"));
}

export function readWatchSeen(): Record<string, string> {
  try {
    const value = JSON.parse(localStorage.getItem(WATCH_SEEN_KEY) ?? "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string")) : {};
  } catch {
    return {};
  }
}

export function markWatchSeen(policyId: string, revision: string): Record<string, string> {
  const next = {...readWatchSeen(), [policyId]: revision};
  localStorage.setItem(WATCH_SEEN_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("bakkwimpyo:watch-seen"));
  return next;
}

export function readCorrectionReceipts(): CorrectionReceipt[] {
  try {
    const value = JSON.parse(localStorage.getItem(CORRECTION_KEY) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is CorrectionReceipt =>
      typeof item?.id === "string" &&
      typeof item?.policyTitle === "string" &&
      typeof item?.issue === "string" &&
      (item?.officialUrl === null || typeof item?.officialUrl === "string") &&
      item?.status === "local_draft" &&
      typeof item?.createdAt === "string"
    );
  } catch {
    return [];
  }
}

export function createCorrectionReceipt(input: {
  policyTitle: string;
  issue: string;
  officialUrl?: string;
  id?: string;
  createdAt?: string;
}): CorrectionReceipt {
  const policyTitle = input.policyTitle.trim().slice(0, 120);
  const issue = input.issue.trim().slice(0, 1000);
  if (policyTitle.length < 2 || issue.length < 5) throw new Error("invalid_correction");
  const rawUrl = input.officialUrl?.trim() ?? "";
  let officialUrl: string | null = null;
  if (rawUrl) {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== "https:") throw new Error("invalid_official_url");
    officialUrl = parsed.toString();
  }
  const receipt: CorrectionReceipt = {
    id: input.id ?? crypto.randomUUID(),
    policyTitle,
    issue,
    officialUrl,
    status: "local_draft",
    createdAt: input.createdAt ?? new Date().toISOString()
  };
  localStorage.setItem(CORRECTION_KEY, JSON.stringify([receipt, ...readCorrectionReceipts()].slice(0, 20)));
  window.dispatchEvent(new Event("bakkwimpyo:corrections"));
  return receipt;
}

export function deleteCorrectionReceipt(id: string): void {
  localStorage.setItem(CORRECTION_KEY, JSON.stringify(readCorrectionReceipts().filter((item) => item.id !== id)));
  window.dispatchEvent(new Event("bakkwimpyo:corrections"));
}
